import { useState, useRef, useEffect, ReactNode } from 'react';
import styles from './SelectorDropdown.module.css';

export interface SelectorDropdownOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

export interface SelectorDropdownProps {
  /** Currently selected value */
  value: string;
  /** List of options */
  options: SelectorDropdownOption[];
  /** Called when selection changes */
  onChange: (value: string) => void;
  /** Icon shown in the trigger button */
  triggerIcon?: ReactNode;
  /** Section header text in dropdown */
  headerLabel?: string;
  /** Title attribute for accessibility */
  title?: string;
  /**
   * `chip` (default) is the standalone control used across the app.
   * `inline` renders the trigger as a word in running text, for a scope line
   * that reads as a sentence describing the document rather than as a row of
   * form controls sitting above it.
   */
  variant?: 'chip' | 'inline';
}

/**
 * Frosted glass selector button with custom dropdown menu.
 * Follows Parrot Design System: --bg-elevated container, crimson active indicator.
 */
export function SelectorDropdown({
  value,
  options,
  onChange,
  triggerIcon,
  headerLabel,
  title,
  variant = 'chip',
}: SelectorDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption?.label || value;

  return (
    <div className={styles.wrapper} data-open={isOpen} data-variant={variant} ref={wrapperRef}>
      {/* Trigger Button */}
      <button
        className={`${styles.trigger} ${variant === 'inline' ? styles.triggerInline : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title={title}
        type="button"
      >
        {triggerIcon && <span className={styles.triggerIcon}>{triggerIcon}</span>}
        <span className={styles.triggerLabel}>{displayLabel}</span>
        <span className={styles.triggerChevron}>
          <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div className={styles.backdrop} onClick={() => setIsOpen(false)} />
          <div className={styles.dropdown}>
            {headerLabel && (
              <div className={styles.dropdownHeader}>{headerLabel}</div>
            )}
            {options.map((option) => (
              <button
                key={option.value}
                className={`${styles.item} ${option.value === value ? styles.itemSelected : ''}`}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                type="button"
              >
                {option.icon && <span className={styles.itemIcon}>{option.icon}</span>}
                <span className={styles.itemLabel}>{option.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
